/**
 * Доля хвоста публичного рендерера: вид `date_picker` (поле даты).
 *
 * WEB-2920, заход 4: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово. Метка `case 'date_picker'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderDatePicker(ctx)`. Замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`); ответ посетителя ветка читает из `values` и пишет
 * через `setValue` — это `useFormValues()` фасада: хук стоит до `switch`, в
 * функции отрисовки его звать нельзя (рисуется одна ветка за рендер). Эту
 * долю импортирует только фасад: она едет в ленивом куске `extended`, а не в
 * первом экране (самопроверка обхода).
 */
import React from 'react';
// WEB-1694: оформление текста ВНУТРИ поля ввода отдельно от подписи над ним
// (порядок силы слоёв — в inputFieldStyle.ts).
import {
  inputLabelStyleOverride,
  inputTextColorOverride,
  inputTextFontSizeOverride,
  // Слово владельца 21.10.2026: вид самого поля (фон, рамка, скругление,
  // высота, отступ, тень, подсказка, рамка в фокусе).
  inputBoxStyleOverride,
  inputLookClassName,
  inputPlaceholderOverlayOverride,
} from '../inputFieldStyle';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';


export function renderDatePicker(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, interp, contentTextColor, values, setValue } = ctx;
  // WEB-688b: native <input type="date"> always shows the browser's own
  // dd/mm/yyyy mask — the `placeholder` attribute is ignored by design
  // for this input type. An overlay span fakes the placeholder while no
  // value is selected; pointerEvents:none lets clicks reach the input
  // underneath so the native picker still opens normally.
  const dateValue = values[block.id] ?? '';
  return (
    <div style={{ marginBottom: 16 }}>
      {block.content && (
        <label
          style={{
            display: 'block',
            fontWeight: 600,
            marginBottom: 8,
            color: 'var(--color-text-primary, inherit)',
            // WEB-1694: свой цвет и кегль ПОДПИСИ — накладкой ПОСЛЕ
            // строки выше. Пустые поля спредят {}: вид прежний до байта.
            ...inputLabelStyleOverride(block),
          }}
        >
          {interp(block.content)}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          type="date"
          required={block.required}
          value={dateValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(block.id, e.target.value)
          }
          data-testid={`public-input-${block.id}`}
          className={inputLookClassName(block)}
          style={{
            width: '100%',
            padding: '10px 14px',
            border: '2px solid var(--color-border-primary, #d9d9d9)',
            borderRadius: 'var(--border-radius-md, 8px)',
            fontSize: 16,
            boxSizing: 'border-box',
            background: 'var(--color-bg-surface, #fff)',
            // WEB-688c: маска/значение наследуют цвет темы воронки —
            // браузерный дефолт выбивался из палитры (тёмная маска на
            // цветном фоне). accentColor красит сам календарь-пикер.
            // WEB-688 QA: пока значения нет и виден overlay-плейсхолдер,
            // прячем нативную маску dd.mm.yyyy (сливались в кашу).
            color:
              !dateValue && block.placeholder
                ? 'transparent'
                : // WEB-1702 п.2: блочный цвет сильнее общего цвета воронки.
                  (contentTextColor ?? 'var(--color-text-primary, inherit)'),
            accentColor: 'var(--color-primary, #4094F7)',
            // Слово владельца 21.10.2026: вид САМОГО поля — накладкой ПОСЛЕ
            // зашитых строк выше; пусто — {}, решает стиль-пак.
            ...inputBoxStyleOverride(block),
            // WEB-1694: кегль текста поля — всегда (иначе накладка-
            // подсказка ниже разъедется с самим полем), а вот цвет —
            // ТОЛЬКО когда маска не спрятана: в состоянии с видимой
            // подсказкой её прячет `transparent` выше (WEB-688 QA), и
            // авторский цвет не имеет права отыграть это назад. Пустые
            // поля спредят {} — вид прежний.
            ...inputTextFontSizeOverride(block),
            ...(!dateValue && block.placeholder
              ? {}
              : inputTextColorOverride(block)),
          }}
        />
        {!dateValue && block.placeholder && (
          <span
            style={{
              position: 'absolute',
              left: 14,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              opacity: 0.6,
              // WEB-1702 п.2: блочный цвет сильнее общего цвета воронки.
              color: contentTextColor ?? 'var(--color-text-secondary, #595959)',
              fontSize: 16,
              // WEB-1694: подсказка внутри поля остаётся ПРИГЛУШЁННОЙ и
              // авторский цвет не наследует (решение записано в
              // inputFieldStyle.ts: иначе подсказку не отличить от
              // набранного значения). А кегль берёт тот же, что поле —
              // иначе накладка встанет не по месту.
              ...inputTextFontSizeOverride(block),
              // Слово владельца 21.10.2026: свой цвет подсказки (без
              // приглушения) и свой отступ поля. Пусто — {}.
              ...inputPlaceholderOverlayOverride(block),
            }}
          >
            {interp(block.placeholder)}
          </span>
        )}
      </div>
    </div>
  );
}
